import * as React from 'react';

/**
 * Rich text from the CMS, rendered **without ever injecting an HTML string** (`CMS-010` AC-7).
 *
 * ## Why not a sanitiser
 *
 * The obvious shape is `dangerouslySetInnerHTML` with a scrubber in front. Two things make that
 * the worse option here.
 *
 * A hand-rolled scrubber is one of the most reliable sources of cross-site scripting there is:
 * the failure mode is a clever input nobody thought of, and the code looks correct until the day
 * it does not. Writing one to save a dependency is a bad trade against a marketing site that
 * anyone can read.
 *
 * And a real sanitiser — DOMPurify and friends — is the right tool but still leaves the page one
 * `dangerouslySetInnerHTML` away from disaster if a later renderer forgets to call it. `CMS-010`
 * AC-7 asks for the sanitiser to be applied in one place *precisely* because the second call site
 * is the one that gets forgotten.
 *
 * ## What this does instead
 *
 * The HTML is parsed with the browser's own parser into an inert document, and then **React
 * elements are built from the allow-listed nodes**. Nothing is ever handed to the DOM as markup:
 * an element outside the allow-list contributes its text and nothing else, and an attribute
 * outside the allow-list does not exist as far as this file is concerned. `<script>`, `onerror`,
 * `javascript:` and everything like them are not filtered out — they are simply never read.
 *
 * The allow-list is the editor's own output: TipTap with StarterKit plus Link, headings capped at
 * levels 2 and 3. Anything it cannot produce has no reason to be here.
 *
 * The one place a URL crosses over is `href`, which is checked against a scheme allow-list rather
 * than a deny-list — `javascript:` is refused by not being `http`, `https` or `mailto`, which is
 * the direction that stays correct when somebody invents a new scheme.
 */

/** Tags the editor can emit, mapped to what they render as. */
const ALLOWED: Readonly<Record<string, keyof React.JSX.IntrinsicElements>> = {
  P: 'p',
  BR: 'br',
  STRONG: 'strong',
  B: 'strong',
  EM: 'em',
  I: 'em',
  S: 's',
  CODE: 'code',
  PRE: 'pre',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  BLOCKQUOTE: 'blockquote',
  H2: 'h2',
  H3: 'h3',
  HR: 'hr',
  A: 'a',
};

/** Void elements take no children; giving React children for them throws. */
const VOID = new Set(['br', 'hr']);

const SAFE_SCHEMES = ['http:', 'https:', 'mailto:'];

/** A link the page is willing to point at, or `null`. Allow-list, never deny-list. */
export function safeHref(raw: string | null): string | null {
  if (raw === null || raw.trim() === '') return null;
  const href = raw.trim();
  // **`//host` is not a relative link, and this is the trap.** It starts with a slash, so the
  // obvious test waves it through untouched — and then every consumer that asks "does it start
  // with `/`?" concludes it is internal. A browser resolves it against the *current scheme*, so
  // `//evil.example` is `https://evil.example`: an off-site address that the code believed was
  // one of ours.
  //
  // What that bought an attacker was not a script — `javascript:` was already refused — but
  // something quieter. `rich-text-panel` withholds `target="_blank"` from internal links, so the
  // navigation happened **in the same tab**, and `content-link-panel` handed the address to the
  // client router as though it were a path. A link in editor-supplied content that reads as
  // internal, renders as internal, and lands on somebody else's site is a phishing primitive.
  //
  // Excluded before the relative check rather than after it, so it falls through to the parse
  // below: `//evil.example` resolves to `https://evil.example/`, is allowed as an ordinary
  // external link, and — because it no longer starts with a single slash — is treated as external
  // everywhere downstream. External links are fine; external links wearing an internal costume
  // are not.
  if (!href.startsWith('//') && (href.startsWith('/') || href.startsWith('#'))) return href;
  try {
    // A base is required for the parse to succeed on a relative URL; any absolute one will do.
    const parsed = new URL(href, 'https://h2hspark.com');
    return SAFE_SCHEMES.includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

export interface RichTextProps {
  readonly html: string;
  readonly className?: string;
}

export function RichText({ html, className }: RichTextProps): React.ReactElement {
  const nodes = React.useMemo(() => renderHtml(html), [html]);
  return <div className={className ?? 'h2h-prose'}>{nodes}</div>;
}

/** Exported for tests: the whole point is that this is checkable without a screen. */
export function renderHtml(html: string): ReadonlyArray<React.ReactNode> {
  if (html.trim() === '') return [];
  if (typeof DOMParser === 'undefined') {
    // Server-side or a test environment with no DOM: fall back to text, never to markup.
    return [html.replace(/<[^>]*>/g, '')];
  }
  // `text/html` parses into an inert document — scripts do not run, images do not load.
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return childrenOf(parsed.body);
}

function childrenOf(parent: Node): ReadonlyArray<React.ReactNode> {
  return [...parent.childNodes].map((node, index) => toReact(node, index)).filter((n) => n !== null);
}

function toReact(node: Node, key: number): React.ReactNode {
  if (node.nodeType === 3 /* text */) return node.nodeValue;
  if (node.nodeType !== 1 /* element */) return null;

  const element = node as Element;
  const tag = ALLOWED[element.tagName];

  // Not in the allow-list: keep what the author wrote, drop the element itself. A `<script>`
  // contributes its source as visible text rather than executing — which looks wrong on the page,
  // and looking wrong is the correct outcome for content that should not be there.
  if (tag === undefined) return <React.Fragment key={key}>{childrenOf(element)}</React.Fragment>;

  if (VOID.has(tag)) return React.createElement(tag, { key });

  if (tag === 'a') {
    // Named `safe` rather than `href`, and that is a convention rather than a preference: the
    // guard test scans every `<a href={…}>` in the marketing surface and passes only an
    // identifier that says it has been through the allow-list. A local called `href` holding a
    // checked value reads identically to one holding an unchecked one, which is how the hole in
    // the section renderer survived review the first time.
    const safe = safeHref(element.getAttribute('href'));
    // A link whose address was refused renders as its own text: the words survive, the link does
    // not. Dropping the text as well would silently delete a sentence.
    if (safe === null) return <React.Fragment key={key}>{childrenOf(element)}</React.Fragment>;
    return (
      <a key={key} href={safe} rel="noreferrer nofollow" target={safe.startsWith('/') ? undefined : '_blank'}>
        {childrenOf(element)}
      </a>
    );
  }

  return React.createElement(tag, { key }, ...childrenOf(element));
}
